import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppNav } from '../../components/layout/AppNav';
import { Badge } from '../../components/ui/Badge';
import { offersService, type Offer } from '../../services/offers.service';
import { formatSAR, formatDate } from '../../utils/formatters';
import { useToast } from '../../components/ui/Toast';
import { Search, FileText, Plus } from 'lucide-react';

const STATUS_FILTERS = ['All', 'Draft', 'Sent', 'Accepted', 'Rejected', 'Expired', 'Closed', 'In Progress', 'Cancelled'];

function isExpired(offer: Offer) {
  if (offer.status === 'Expired') return true;
  if (offer.status === 'Sent' && offer.customerLinkExpiresAt) {
    return new Date(offer.customerLinkExpiresAt).getTime() < Date.now();
  }
  return false;
}

export const MyOffersPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  useEffect(() => {
    offersService
      .list()
      .then(setOffers)
      .catch(() => showToast('Failed to load offers', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);

  const filtered = offers.filter((o) => {
    const matchSearch =
      o.offerNumber.toLowerCase().includes(search.toLowerCase()) ||
      (o.customer?.nameEn ?? '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All'
      ? true
      : statusFilter === 'Expired'
        ? isExpired(o)
        : o.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const availableFilters = STATUS_FILTERS.filter((f) => {
    if (f === 'All') return true;
    if (f === 'Expired') return offers.some(isExpired);
    return offers.some((o) => o.status === f);
  });

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="My Offers" subtitle="All offers you have created" />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>

        {/* Header */}
        <div className="page-header">
          <div>
            <div className="page-title">My Offers</div>
            <div style={{ fontSize: '13px', color: 'var(--text-2)', marginTop: '2px' }}>
              {loading ? 'Loading…' : `${filtered.length} offer${filtered.length !== 1 ? 's' : ''}`}
            </div>
          </div>
          <button className="btn-primary" onClick={() => navigate('/offers/wizard/location')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Plus size={15} />
            New Offer
          </button>
        </div>

        {/* Search + status filter */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: '280px' }}>
            <Search size={15} color="var(--text-3)" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="search"
              placeholder="Search offers…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="inp"
              style={{ paddingLeft: '36px' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {availableFilters.map((f) => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`filter-chip${statusFilter === f ? ' active' : ''}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="card">
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>
              <div className="skeleton" style={{ height: '14px', width: '200px', margin: '0 auto' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><FileText size={24} /></div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-1)', marginBottom: '4px' }}>No offers found</div>
              <div style={{ fontSize: '13px', color: 'var(--text-2)' }}>
                {search || statusFilter !== 'All' ? 'Try adjusting your search or filters.' : 'Start by creating your first offer.'}
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Offer #</th>
                    <th>Customer</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Created</th>
                    <th style={{ width: '80px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((offer) => (
                    <tr key={offer.id} onClick={() => navigate(`/my-offers/${offer.id}`)} style={{ cursor: 'pointer' }}>
                      <td style={{ fontWeight: 700, color: 'var(--p)' }}>{offer.offerNumber}</td>
                      <td>{offer.customer?.nameEn ?? <span style={{ color: 'var(--text-3)' }}>—</span>}</td>
                      <td><Badge label={isExpired(offer) && offer.status !== 'Expired' ? 'Expired' : offer.status} /></td>
                      <td style={{ fontWeight: 600 }}>{formatSAR(offer.total)}</td>
                      <td style={{ color: 'var(--text-2)', fontSize: '13px', whiteSpace: 'nowrap' }}>{formatDate(offer.createdAt)}</td>
                      <td>
                        <button
                          className="btn-ghost btn-xs"
                          onClick={(e) => { e.stopPropagation(); navigate(`/my-offers/${offer.id}`); }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
